/**
 * Cross-component shared state. Lightweight Svelte stores; runes-compatible
 * via the $store auto-subscription syntax in components.
 */
import { writable, type Writable } from "svelte/store";
import type { DerivedWorkout } from "./lapAnalysis";
import type { SwimFirehoseResponse, LapLabel } from "@contract/types";

/** "health-off": the swimmer declined Health access. An answer, not an outage: no
 *  "saved copy" pill, and the Swim tab's empty card says how to turn it on. */
export type OfflineReason = "ok" | "no-handler" | "timeout" | "rejected" | "stub" | "health-off";

export const offline: Writable<OfflineReason> = writable("ok");

/**
 * Currently-selected swim UUID. `null` means "most recent swim" — the firehose
 * call without a workoutUUID returns whatever HealthKit considers latest.
 * Set from WorkoutRow taps; read by Prototypes (refetches on change) and
 * App.svelte (auto-navigates to the Swim tab on selection).
 */
export const selectedWorkoutUUID: Writable<string | null> = writable(null);

/**
 * Currently-selected Club session id (the shared "morning" on screen). `null`
 * means the most-recent session — the exact mirror of selectedWorkoutUUID for
 * the Swim tab. Set by ClubSessionHeader's arrows; read by SharedMorningView
 * (via currentSession). Sessions are DERIVED, so a stale id resolves to the most
 * recent (currentSession's offline-first fallback), never an error.
 */
export const selectedSessionId: Writable<string | null> = writable(null);

/**
 * Derived view of the currently-displayed swim. Written by Prototypes after
 * its firehose fetch resolves; read by MetricRings (Peak HR + Silk Sets
 * rings), SetRibbonSection (Swim-tab set ribbon), and OverlayHost (dw for
 * SetDetailSheet) — plus any other surface that needs the active swim's
 * analytics. `null` during load, after error, or when there is no swim to show.
 */
export const currentDerivedWorkout: Writable<DerivedWorkout | null> = writable(null);

/** The live persisted lap labels (corrections + exclusions). Single source of
 *  truth for the shipped app: seeded from the bridge on load (Prototypes), and
 *  the array every label write upserts onto. */
export const currentLabels: Writable<LapLabel[]> = writable([]);

/**
 * Raw firehose payload for the currently-displayed swim. Paired with
 * currentDerivedWorkout — both written by Prototypes after each fetch.
 * Consumed by the FAB share flow (needs the full JSON for navigator.share /
 * clipboard / download fallback).
 */
export const currentRawFirehose: Writable<SwimFirehoseResponse | null> = writable(null);

/**
 * Explicit fetch lifecycle for the currently-displayed swim. Published by
 * Prototypes on every firehose fetch; mirrored by Firehose so the raw browser
 * can distinguish "still loading" from "no swim found" from "fetch failed"
 * instead of conflating all of them into a single null spinner. (HYG-05)
 */
export type FirehoseStatus = "loading" | "ready" | "empty" | "error";
export const currentFirehoseStatus: Writable<FirehoseStatus> = writable("loading");

/**
 * Persistence status for the most-recent lap-label write. Driven by
 * commitLabelChange / persistWithRetry in liveCorrection.ts. Components read
 * this to show a truthful save indicator (e.g. saving spinner → saved tick →
 * failed banner). Only the latest commitId may update this store — stale
 * in-flight calls are silently discarded.
 */
export type SaveStatus = "idle" | "saving" | "saved" | "failed";
export const saveStatus: Writable<SaveStatus> = writable("idle");

/**
 * Persistence status for the most-recent ring-goal write. The goal twin of
 * saveStatus, driven by commitGoalChange in goalCorrection.ts. Goal saves now
 * mirror lap-label durability (journal-before-write + read-back verify), so they
 * feed the same save-trouble pill through this status + goalJournalPendingCount.
 */
export const goalSaveStatus: Writable<SaveStatus> = writable("idle");

/**
 * Reactive mirror of the durable goal journal's size (goalJournal.ts; 0 or 1 —
 * goals are a singleton). Kept in sync by goalCorrection.syncGoalJournalCount on
 * every write/clear/replay and on boot. Read (alongside journalPendingCount) by
 * the shared save-trouble pill so a stranded goal edit surfaces.
 */
export const goalJournalPendingCount: Writable<number> = writable(0);

/**
 * True when the LAST label load for the current swim FAILED — the bridge
 * getLapLabels call rejected, which is DISTINCT from a swim that genuinely has
 * no labels (loads as []). While true, commitLabelChange refuses to write so a
 * correction can't full-replace and WIPE the labels we couldn't read back
 * (C2a). Set by Prototypes on each swim load: false on success, true on failure.
 */
export const labelsLoadFailed: Writable<boolean> = writable(false);

/**
 * Reactive mirror of the durable label journal's size (labelJournal.ts). Kept in
 * sync by liveCorrection.syncJournalCount on every append/remove/replay and on
 * boot. Read by the app-level save-trouble pill (saveIndicator.ts) so a stranded
 * correction surfaces. `> 0` means "there is a correction whose write is not yet
 * confirmed on disk."
 */
export const journalPendingCount: Writable<number> = writable(0);

/**
 * True while liveCorrection.replayJournal() has an actual replay pass running.
 * Set/cleared by replayJournal itself (boot, the auth-resolved nudge, and the
 * save-trouble pill's "Try now" button all call the same function). Read by
 * saveIndicator.ts to drive the pill's "Retrying..." / disabled state so an
 * impatient extra tap gets feedback instead of silently doing nothing.
 */
export const journalReplaying: Writable<boolean> = writable(false);
